import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { toast } from "react-toastify";
import subscriptionRequests from "../../api/SubscriptionRequests";
import Navbar from "../../components/dashboard/Navbar";
import Loader from "../../components/utils/Loader";
import {
  Crown,
  Plus,
  Edit3,
  Trash2,
  Eye,
  TrendingUp,
  DollarSign,
  Package,
  CheckCircle,
  XCircle,
  Target,
  Calendar,
} from "lucide-react";

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    details: [
      { name: "1 month", unit: "", price: "" },
      { name: "2 months", unit: "", price: "" },
      { name: "3 months", unit: "", price: "" },
      { name: "No Expiry", unit: "", price: "" }
    ]
  });

  // Available plan names
  const availablePlanNames = ["Bronze", "Silver", "Gold", "Platinum"];
  const durationOptions = ["1 month", "2 months", "3 months", "No Expiry"];

  // Get used plan names
  const usedPlanNames = plans.map((plan) => plan.name);

  // Get available plan names (not yet used)
  const unusedPlanNames = availablePlanNames.filter(
    (name) => !usedPlanNames.includes(name)
  );

  // Check if maximum plans reached
  const maxPlansReached = plans.length >= 4;
  const [formLoading, setFormLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch plans
  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await subscriptionRequests.getAllPlans();
      const data = response?.data;
      //console.log("API Response:", data);
      if (data.data.status_code === "000") {
        //console.log("Setting plans:", data.data.data);
        setPlans(data.data.data);
      } else {
        toast.error(data.data.reason);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      toast.error(
        error?.response?.data?.reason || "Error fetching subscription plans"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDetailChange = (index, field, value) => {
    const updatedDetails = [...formData.details];
    updatedDetails[index] = {
      ...updatedDetails[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      details: updatedDetails
    }));
  };

  // Handle add plan
  const handleAddPlan = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      // Filter out empty details and prepare data for API
      const planData = {
        name: formData.name,
        details: formData.details.filter(detail => detail.unit && detail.price)
      };

      const response = await subscriptionRequests.createPlan(planData);
      const data = response?.data;
      //console.log("data", data);
      if (data.data.status_code === "001") {
        toast.success("Subscription plan created successfully!");
        setShowAddModal(false);
        setFormData({
          name: "",
          details: [
            { name: "1 month", unit: "", price: "" },
            { name: "2 months", unit: "", price: "" },
            { name: "3 months", unit: "", price: "" },
            { name: "No Expiry", unit: "", price: "" }
          ]
        });
        fetchPlans();
      } else {
        toast.error(data.data.reason);
      }
    } catch (error) {
      console.error("Error creating plan:", error);
      toast.error("Failed to create subscription plan");
    } finally {
      setFormLoading(false);
    }
  };

  // Handle edit plan
  const handleEditPlan = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const updateData = {
        details: formData.details
      };

      const response = await subscriptionRequests.updatePlan(
        selectedPlan.packageId,
        updateData
      );
      const data = response?.data;
      if (data.data.status_code === "000") {
        toast.success("Subscription plan updated successfully!");
        setShowEditModal(false);
        setSelectedPlan(null);
        setSelectedDetail(null);
        setFormData({
          name: "",
          details: [
            { name: "1 month", unit: "", price: "" },
            { name: "2 months", unit: "", price: "" },
            { name: "3 months", unit: "", price: "" },
            { name: "No Expiry", unit: "", price: "" }
          ]
        });
        fetchPlans();
      } else {
        toast.error(data.data.reason || "Failed to update plan");
      }
    } catch (error) {
      console.error("Error updating plan:", error);
      toast.error("Failed to update subscription plan");
    } finally {
      setFormLoading(false);
    }
  };

  // Handle delete plan
  const handleDeletePlan = async () => {
    setDeleteLoading(true);
    try {
      const response = await subscriptionRequests.deletePlan(
        selectedPlan.packageId
      );
      const data = response?.data;
      if (data.data.status_code === "000") {
        toast.success("Subscription plan deleted successfully!");
        setShowDeleteModal(false);
        setSelectedPlan(null);
        fetchPlans();
      } else {
        toast.error(data.data.reason);
      }
    } catch (error) {
      console.error("Error deleting plan:", error);
      toast.error("Failed to delete subscription plan");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Open edit modal
  const openEditModal = (plan) => {
    setSelectedPlan(plan);
    setFormData({
      name: plan.name,
      details: plan.details.map(detail => ({
        name: detail.name,
        unit: detail.unit.toString(),
        price: detail.price.toString()
      }))
    });
    setShowEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (plan) => {
    setSelectedPlan(plan);
    setShowDeleteModal(true);
  };

  // Open view modal
  const openViewModal = (plan) => {
    setSelectedPlan(plan);
    setShowViewModal(true);
  };

  // Get plan color scheme
  const getPlanColorScheme = (planName) => {
    const name = planName.toLowerCase();
    if (name.includes("bronze"))
      return {
        bg: "from-amber-500 to-orange-600",
        text: "text-amber-600",
        light: "bg-amber-50",
      };
    if (name.includes("silver"))
      return {
        bg: "from-gray-400 to-gray-600",
        text: "text-gray-600",
        light: "bg-gray-50",
      };
    if (name.includes("gold"))
      return {
        bg: "from-yellow-400 to-yellow-600",
        text: "text-yellow-600",
        light: "bg-yellow-50",
      };
    if (name.includes("platinum"))
      return {
        bg: "from-purple-500 to-indigo-600",
        text: "text-purple-600",
        light: "bg-purple-50",
      };
    return {
      bg: "from-blue-500 to-indigo-600",
      text: "text-blue-600",
      light: "bg-blue-50",
    };
  };

  // Calculate stats - now considering all details across all plans
  const stats = {
    total: plans.length,
    totalUnits: plans.reduce((sum, plan) => {
      return sum + plan.details.reduce((planSum, detail) => {
        return planSum + (Number(detail.unit) || 0);
      }, 0);
    }, 0),
    totalRevenue: plans.reduce((sum, plan) => {
      return sum + plan.details.reduce((planSum, detail) => {
        return planSum + (Number(detail.price) || 0);
      }, 0);
    }, 0),
    averagePrice:
      plans.length > 0
        ? (
            plans.reduce((sum, plan) => 
              sum + plan.details.reduce((planSum, detail) => 
                planSum + (Number(detail.price) || 0), 0), 0) / 
            (plans.length * 4) // Assuming 4 details per plan
          ).toFixed(2)
        : 0,
  };

  if (loading && plans.length === 0) {
    return (
      <>
        <Navbar />
        <div className="flex justify-center items-center min-h-screen bg-gray-50">
          <Loader />
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
        <div className="px-4 py-8 mx-auto max-w-8xl sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex gap-3 items-start mb-3">
                  <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg">
                    <Crown className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">
                      Subscription Plans
                    </h1>
                    <p className="text-gray-600">
                      Manage and configure subscription packages for your users
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 items-center text-sm">
                  <span className="flex gap-2 items-center font-medium text-purple-600">
                    <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                    {plans.length} of 4 plans
                  </span>
                  <span className="text-gray-400">|</span>
                  <span className="text-gray-500">
                    Total units: {(stats.totalUnits || 0).toLocaleString()}
                  </span>
                  {maxPlansReached && (
                    <>
                      <span className="text-gray-400">|</span>
                      <span className="flex gap-1 items-center font-medium text-amber-600">
                        <div className="w-2 h-2 bg-amber-600 rounded-full"></div>
                        Maximum plans reached
                      </span>
                    </>
                  )}
                </div>
              </div>
              <motion.button
                whileHover={!maxPlansReached ? { scale: 1.05 } : {}}
                whileTap={!maxPlansReached ? { scale: 0.95 } : {}}
                onClick={() => !maxPlansReached && setShowAddModal(true)}
                disabled={maxPlansReached}
                className={`flex gap-2 items-center px-6 py-3 text-sm font-medium rounded-xl shadow-lg transition-all duration-200 ${
                  maxPlansReached
                    ? "text-gray-400 bg-gray-200 cursor-not-allowed"
                    : "text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                }`}
              >
                <Plus className="w-4 h-4" />
                {maxPlansReached ? "Maximum Plans Reached" : "Add New Plan"}
              </motion.button>
            </div>
          </motion.div>

          {/* Stats Dashboard */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="p-6 bg-white rounded-xl border border-gray-100 shadow-lg transition-shadow duration-300 hover:shadow-xl"
              >
                <div className="flex gap-4 items-center">
                  <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-md">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600">
                      Total Plans
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      {stats.total}
                    </p>
                    <p className="text-xs text-gray-500">of 4 maximum</p>
                  </div>
                </div>
                {maxPlansReached && (
                  <div className="p-2 mt-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-xs font-medium text-amber-700">
                      Maximum plans reached
                    </p>
                  </div>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="p-6 bg-white rounded-xl border border-gray-100 shadow-lg transition-shadow duration-300 hover:shadow-xl"
              >
                <div className="flex gap-4 items-center">
                  <div className="p-3 bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-md">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600">
                      Total Units
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      {(stats.totalUnits || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="p-6 bg-white rounded-xl border border-gray-100 shadow-lg transition-shadow duration-300 hover:shadow-xl"
              >
                <div className="flex gap-4 items-center">
                  <div className="p-3 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl shadow-md">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600">
                      Total Pricing
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      GH₵{(stats.totalRevenue || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="p-6 bg-white rounded-xl border border-gray-100 shadow-lg transition-shadow duration-300 hover:shadow-xl"
              >
                <div className="flex gap-4 items-center">
                  <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-md">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600">
                      Avg. Price
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      GH₵{stats.averagePrice}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Plan Names Status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-6"
          >
            <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-lg">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Plan Names Status
              </h3>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {availablePlanNames.map((planName) => {
                  const isUsed = usedPlanNames.includes(planName);
                  return (
                    <div
                      key={planName}
                      className={`p-3 rounded-lg border-2 ${
                        isUsed
                          ? "bg-gray-50 border-gray-200"
                          : "bg-red-50 border-red-200"
                      }`}
                    >
                      <div className="flex gap-2 items-center">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            isUsed ? "bg-gray-400" : "bg-red-500"
                          }`}
                        ></div>
                        <span
                          className={`text-sm font-medium ${
                            isUsed ? "text-gray-600" : "text-red-700"
                          }`}
                        >
                          {planName}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        {isUsed ? "Used" : "Available"}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Plans Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4"
          >
            {plans.map((plan, index) => {
              const colorScheme = getPlanColorScheme(plan.name);
              // Calculate min price for display
              const minPriceDetail = plan.details.reduce((min, detail) => 
                Number(detail.price) < Number(min.price) ? detail : min
              );
              
              return (
                <motion.div
                  key={plan.packageId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-lg transition-all duration-300 hover:shadow-xl group"
                >
                  {/* Plan Header */}
                  <div
                    className={`bg-gradient-to-br ${colorScheme.bg} p-6 text-white relative overflow-hidden`}
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full opacity-10 translate-x-16 -translate-y-16"></div>
                    <div className="relative z-10">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-bold">{plan.name}</h3>
                        <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                          <Crown className="w-5 h-5" />
                        </div>
                      </div>
                      <div className="mb-1 text-3xl font-bold">
                        GH₵{(minPriceDetail.price || 0).toLocaleString()}
                      </div>
                      <div className="text-sm opacity-90">
                        Starting from • {(minPriceDetail.unit || 0).toLocaleString()} units
                      </div>
                    </div>
                  </div>

                  {/* Plan Details */}
                  <div className="p-6">
                    <div className="space-y-3 mb-4">
                      {plan.details.map((detail, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                          <div className="flex gap-2 items-center">
                            <Calendar className="w-3 h-3 text-gray-500" />
                            <span className="text-xs font-medium text-gray-700">{detail.name}</span>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-semibold text-gray-900">
                              GH₵{(detail.price || 0).toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">
                              {(detail.unit || 0).toLocaleString()} units
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2 items-center pt-4 border-t border-gray-100">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => openViewModal(plan)}
                        className="flex flex-1 gap-2 justify-center items-center px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg transition-colors hover:bg-blue-100"
                      >
                        <Eye className="w-3 h-3" />
                        View
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => openEditModal(plan)}
                        className="flex flex-1 gap-2 justify-center items-center px-3 py-2 text-xs font-medium text-red-600 bg-red-50 rounded-lg transition-colors hover:bg-red-100"
                      >
                        <Edit3 className="w-3 h-3" />
                        Edit
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => openDeleteModal(plan)}
                        className="flex flex-1 gap-2 justify-center items-center px-3 py-2 text-xs font-medium text-red-600 bg-red-50 rounded-lg transition-colors hover:bg-red-100"
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Empty State */}
          {plans.length === 0 && !loading && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="py-12 text-center"
            >
              <div className="flex justify-center items-center mx-auto mb-6 w-24 h-24 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full">
                <Package className="w-12 h-12 text-purple-600" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-gray-900">
                No Subscription Plans
              </h3>
              <p className="mb-6 text-gray-600">
                Get started by creating your first subscription plan
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAddModal(true)}
                className="inline-flex gap-2 items-center px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl shadow-lg transition-all duration-200 hover:from-purple-700 hover:to-indigo-700"
              >
                <Plus className="w-4 h-4" />
                Create First Plan
              </motion.button>
            </motion.div>
          )}
        </div>
      </div>

      {/* Add Plan Modal */}
      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 z-[9999] overflow-y-auto">
            <div className="flex justify-center items-center px-4 pt-4 pb-20 min-h-screen text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 z-[9998]"
                onClick={() => setShowAddModal(false)}
              ></div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative z-[9999] inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl"
              >
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Add New Plan
                  </h3>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleAddPlan} className="space-y-6">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Plan Name
                    </label>
                    <select
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="px-4 py-3 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Select a plan name</option>
                      {unusedPlanNames.map((planName) => (
                        <option key={planName} value={planName}>
                          {planName}
                        </option>
                      ))}
                    </select>
                    {unusedPlanNames.length === 0 && (
                      <p className="mt-1 text-xs text-red-600">
                        All plan names have been used. Delete a plan to create a new one.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block mb-4 text-sm font-medium text-gray-700">
                      Plan Durations & Pricing
                    </label>
                    <div className="space-y-4">
                      {formData.details.map((detail, index) => (
                        <div key={index} className="p-4 border border-gray-200 rounded-lg">
                          <div className="flex gap-2 items-center mb-3">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="font-medium text-gray-700">{detail.name}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block mb-1 text-xs font-medium text-gray-600">
                                Units
                              </label>
                              <input
                                type="number"
                                value={detail.unit}
                                onChange={(e) => handleDetailChange(index, 'unit', e.target.value)}
                                required
                                min="1"
                                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="Number of units"
                              />
                            </div>
                            <div>
                              <label className="block mb-1 text-xs font-medium text-gray-600">
                                Price (GH₵)
                              </label>
                              <input
                                type="number"
                                value={detail.price}
                                onChange={(e) => handleDetailChange(index, 'price', e.target.value)}
                                required
                                min="0"
                                step="0.01"
                                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="Price in Ghana Cedis"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={formLoading}
                      className="flex-1 px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg transition-colors hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50"
                    >
                      {formLoading ? "Creating..." : "Create Plan"}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Plan Modal */}
      <AnimatePresence>
        {showEditModal && selectedPlan && (
          <div className="fixed inset-0 z-[9999] overflow-y-auto">
            <div className="flex justify-center items-center px-4 pt-4 pb-20 min-h-screen text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 z-[9998]"
                onClick={() => setShowEditModal(false)}
              ></div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative z-[9999] inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl"
              >
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Edit Plan
                    </h3>
                    {selectedPlan && (
                      <p className="text-sm text-gray-500">
                        Currently editing: {selectedPlan.name}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleEditPlan} className="space-y-6">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Plan Name
                    </label>
                    <input
                      type="text"
                      value={selectedPlan?.name || ""}
                      disabled
                      className="px-4 py-3 w-full text-gray-600 bg-gray-50 rounded-lg border border-gray-300 cursor-not-allowed"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Plan names cannot be changed once created
                    </p>
                  </div>

                  <div>
                    <label className="block mb-4 text-sm font-medium text-gray-700">
                      Plan Durations & Pricing
                    </label>
                    <div className="space-y-4">
                      {formData.details.map((detail, index) => (
                        <div key={index} className="p-4 border border-gray-200 rounded-lg">
                          <div className="flex gap-2 items-center mb-3">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="font-medium text-gray-700">{detail.name}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block mb-1 text-xs font-medium text-gray-600">
                                Units
                              </label>
                              <input
                                type="number"
                                value={detail.unit}
                                onChange={(e) => handleDetailChange(index, 'unit', e.target.value)}
                                required
                                min="1"
                                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              />
                            </div>
                            <div>
                              <label className="block mb-1 text-xs font-medium text-gray-600">
                                Price (GH₵)
                              </label>
                              <input
                                type="number"
                                value={detail.price}
                                onChange={(e) => handleDetailChange(index, 'price', e.target.value)}
                                required
                                min="0"
                                step="0.01"
                                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={formLoading}
                      className="flex-1 px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-red-700 rounded-lg transition-colors hover:from-red-700 hover:to-red-800 disabled:opacity-50"
                    >
                      {formLoading ? "Updating..." : "Update Plan"}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && selectedPlan && (
          <div className="fixed inset-0 z-[9999] overflow-y-auto">
            <div className="flex justify-center items-center px-4 pt-4 pb-20 min-h-screen text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 z-[9998]"
                onClick={() => setShowDeleteModal(false)}
              ></div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative z-[9999] inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl"
              >
                <div className="text-center">
                  <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full">
                    <Trash2 className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-gray-900">
                    Delete Plan
                  </h3>
                  <p className="mb-6 text-gray-600">
                    Are you sure you want to delete the{" "}
                    <strong>{selectedPlan.name}</strong> plan? This action
                    cannot be undone.
                  </p>
                  <div className="p-3 mb-6 bg-gray-50 rounded-lg">
                    <div className="mb-1 text-xs text-gray-600">Package ID</div>
                    <div className="font-mono text-sm text-gray-900 break-all">
                      #{selectedPlan.packageId}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDeleteModal(false)}
                      className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeletePlan}
                      disabled={deleteLoading}
                      className="flex-1 px-4 py-3 text-sm font-medium text-white bg-red-600 rounded-lg transition-colors hover:bg-red-700 disabled:opacity-50"
                    >
                      {deleteLoading ? "Deleting..." : "Delete Plan"}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* View Plan Modal */}
      <AnimatePresence>
        {showViewModal && selectedPlan && (
          <div className="fixed inset-0 z-[9999] overflow-y-auto">
            <div className="flex justify-center items-center px-4 pt-4 pb-20 min-h-screen text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 z-[9998]"
                onClick={() => setShowViewModal(false)}
              ></div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative z-[9999] inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl"
              >
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Plan Details
                  </h3>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Plan Header */}
                  <div
                    className={`bg-gradient-to-br ${
                      getPlanColorScheme(selectedPlan.name).bg
                    } p-4 rounded-xl text-white`}
                  >
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-xl font-bold">{selectedPlan.name}</h4>
                      <Crown className="w-6 h-6" />
                    </div>
                    <div className="text-base opacity-90">
                      Multiple duration options available
                    </div>
                  </div>

                  {/* Plan Details */}
                  <div className="space-y-3">
                    {selectedPlan.details.map((detail, index) => (
                      <div key={index} className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex gap-2 items-center">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="font-medium text-gray-700">{detail.name}</span>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">
                              GH₵{(detail.price || 0).toLocaleString()}
                            </div>
                            <div className="text-sm text-gray-600">
                              {(detail.unit || 0).toLocaleString()} units
                            </div>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">
                          Price per unit: GH₵{((detail.price || 0) / (detail.unit || 1)).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Package ID */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="mb-1 text-xs text-gray-600">Package ID</div>
                    <div className="font-mono text-xs text-gray-900 break-all">
                      #{selectedPlan.packageId}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SubscriptionPlans;
